/**
 * GridRow Component - Single row in the sequencer grid
 * Contains instrument controls and a row of cells
 */

import { useMemo, useCallback, useState } from 'react';
import { GridCell } from './GridCell';
import { InstrumentSelector } from './InstrumentSelector';
import { InstrumentParameters } from './InstrumentParameters';
import { VolumeSlider } from './VolumeSlider';
import { MuteButton } from './MuteButton';
import type { GridRowProps } from '../../types/grid';
import type { InstrumentParameters as IParams } from '../../types/instruments';

/**
 * GridRow component
 * Renders a complete row with:
 * - Instrument selector
 * - Mute button
 * - Remove button
 * - Volume slider
 * - Array of grid cells
 * 
 * @param props - Row properties
 * @returns GridRow component
 */
export function GridRow({
  row,
  channel,
  steps,
  currentStep,
  gridState,
  noteState,
  onCellToggle,
  onNoteChange,
  onInstrumentChange,
  onParametersChange,
  onParametersEnabledChange,
  onVolumeChange,
  onMuteToggle,
  onRemoveRow,
}: GridRowProps) {
  const [isRemoving, setIsRemoving] = useState(false);
  const [isParametersExpanded, setIsParametersExpanded] = useState(false);

  /**
   * Handle row removal with animation
   */
  const handleRemove = useCallback(() => {
    setIsRemoving(true);
    // Wait for animation to complete before actually removing
    setTimeout(() => {
      onRemoveRow();
    }, 400); // Match animation duration
  }, [onRemoveRow]);

  /**
   * Handle instrument change
   */
  const handleInstrumentChange = useCallback(
    (instrument: import('../../types/composition').InstrumentType, parameters?: import('../../types/instruments').InstrumentParameters) => {
      onInstrumentChange(instrument, parameters);
    },
    [onInstrumentChange]
  );

  /**
   * Handle cell toggle
   */
  const handleCellToggle = useCallback(
    (step: number) => {
      onCellToggle(step);
    },
    [onCellToggle]
  );

  /**
   * Handle note edit
   */
  const handleNoteEdit = useCallback(
    (step: number) => {
      const currentNote = noteState[step] || 'C4';
      
      const newNote = prompt(
        `🎵 Edit Note (Row ${row + 1}, Step ${step + 1})\n\nExamples: C4, D#5, F3, A2\n\nCurrent note:`,
        currentNote
      );
      
      if (newNote && newNote.trim()) {
        onNoteChange(step, newNote.trim());
      }
    },
    [row, noteState, onNoteChange]
  );

  /**
   * Memoize connected cell calculations
   * This prevents recalculating on every render
   */
  const connectedStates = useMemo(() => {
    const states: Array<{ left: boolean; right: boolean }> = [];
    
    for (let step = 0; step < steps; step++) {
      const isConnectedLeft = step > 0 && !!(gridState[step] && gridState[step - 1]);
      const isConnectedRight = step < steps - 1 && !!(gridState[step] && gridState[step + 1]);
      
      states.push({ left: isConnectedLeft, right: isConnectedRight });
    }
    
    return states;
  }, [gridState, steps]);

  /**
   * Handle parameter changes
   */
  const handleParametersChange = useCallback(
    (parameters: IParams) => {
      if (onParametersChange) {
        onParametersChange(parameters);
      }
    },
    [onParametersChange]
  );

  return (
    <div style={{ width: '100%' }}>
      <div className={`grid-row ${isRemoving ? 'removing' : ''}`}>
        {/* Row Controls - Sticky to left */}
        <div className="row-controls">
          {/* Instrument Selector */}
          <InstrumentSelector
            value={channel.instrument}
            onChange={handleInstrumentChange}
            row={row}
          />

          {/* Mute Button */}
          <MuteButton muted={channel.muted} onToggle={onMuteToggle} row={row} />

          {/* Volume Slider */}
          <VolumeSlider value={channel.volume} onChange={onVolumeChange} row={row} />

          {/* Parameters Toggle Button */}
          {onParametersChange && onParametersEnabledChange && (
            <button
              className="param-settings-btn"
              onClick={() => setIsParametersExpanded(!isParametersExpanded)}
              aria-label={`${isParametersExpanded ? 'Hide' : 'Show'} sound parameters for row ${row + 1}`}
              aria-expanded={isParametersExpanded}
              title={`${isParametersExpanded ? 'Hide' : 'Show'} sound parameters`}
              type="button"
            >
              <i className="fas fa-sliders-h"></i>
            </button>
          )}

          {/* Remove Row Button */}
          <button
            className="remove-row-btn"
            onClick={handleRemove}
            aria-label={`Remove row ${row + 1}`}
            type="button"
            disabled={isRemoving}
          >
            ✖
          </button>
        </div>

        {/* Notes Wrapper - Scrollable container for cells */}
        <div className="notes-wrapper">
          {Array.from({ length: steps }, (_, step) => (
            <GridCell
              key={step}
              row={row}
              step={step}
              active={gridState[step] ?? false}
              note={noteState[step] ?? null}
              isCurrentStep={step === currentStep}
              isConnectedLeft={connectedStates[step]?.left ?? false}
              isConnectedRight={connectedStates[step]?.right ?? false}
              onToggle={() => handleCellToggle(step)}
              onNoteEdit={() => handleNoteEdit(step)}
              onNoteChange={(newNote) => onNoteChange(step, newNote)}
            />
          ))}
        </div>
      </div>

      {/* Instrument Parameters - Collapsible panel below the row */}
      {onParametersChange && onParametersEnabledChange && isParametersExpanded && (
        <InstrumentParameters
          instrument={channel.instrument}
          parameters={channel.parameters || { adsr: { attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.1 } }}
          onChange={handleParametersChange}
          parametersEnabled={channel.parametersEnabled ?? false}
          onParametersEnabledChange={onParametersEnabledChange}
          row={row}
          isExpanded={true}
        />
      )}
    </div>
  );
}
